// ==============================================================
// Notification Worker Utility
// Handles service worker registration and push notifications
// ==============================================================

const SW_PATH = "/sw.js";
const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || "";

// ==============================================================
// Service Worker Registration
// ==============================================================

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) {
    console.warn("[NotificationWorker] Service workers not supported");
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register(SW_PATH, {
      scope: "/",
    });

    console.log("[NotificationWorker] Service Worker registered:", registration);

    // Handle updates
    registration.addEventListener("updatefound", () => {
      const newWorker = registration.installing;
      if (newWorker) {
        newWorker.addEventListener("statechange", () => {
          if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
            // New service worker available
            console.log("[NotificationWorker] New service worker available");
            // Optionally notify user to refresh
          }
        });
      }
    });

    return registration;
  } catch (error) {
    console.error("[NotificationWorker] Service Worker registration failed:", error);
    return null;
  }
}

// ==============================================================
// Push Notification Subscription
// ==============================================================

export async function subscribeToPushNotifications(
  userId: string
): Promise<PushSubscription | null> {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    console.warn("[NotificationWorker] Push notifications not supported");
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.ready;

    // Check existing subscription
    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      // Create new subscription
      const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource;

      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });

      // Send subscription to backend
      await sendSubscriptionToBackend(subscription, userId);
    }

    console.log("[NotificationWorker] Push subscription:", subscription);
    return subscription;
  } catch (error) {
    console.error("[NotificationWorker] Push subscription failed:", error);
    return null;
  }
}

export async function unsubscribeFromPushNotifications(): Promise<boolean> {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      await subscription.unsubscribe();
      // Notify backend
      const { notificationsAPI } = await import("@/lib/services/api.service");
      await notificationsAPI.unsubscribePush();
      return true;
    }

    return false;
  } catch (error) {
    console.error("[NotificationWorker] Unsubscribe failed:", error);
    return false;
  }
}

// ==============================================================
// Background Sync
// ==============================================================

export async function syncNotificationsInBackground(): Promise<void> {
  if (!("serviceWorker" in navigator) || !("sync" in (self as any).registration)) {
    console.warn("[NotificationWorker] Background sync not supported");
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    await (registration as any).sync.register("sync-notifications");
    console.log("[NotificationWorker] Background sync registered");
  } catch (error) {
    console.error("[NotificationWorker] Background sync failed:", error);
  }
}

export async function syncUnreadCountInBackground(): Promise<void> {
  if (!("serviceWorker" in navigator) || !("sync" in (self as any).registration)) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    await (registration as any).sync.register("sync-unread-count");
  } catch (error) {
    console.error("[NotificationWorker] Background sync failed:", error);
  }
}

// ==============================================================
// Badge Management
// ==============================================================

export async function updateBadge(count: number): Promise<void> {
  if ("setAppBadge" in navigator) {
    try {
      if (count > 0) {
        await navigator.setAppBadge(count);
      } else {
        await navigator.clearAppBadge();
      }
    } catch (error) {
      console.error("[NotificationWorker] Badge update failed:", error);
    }
  }
}

// ==============================================================
// Helper Functions
// ==============================================================

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = window.atob(base64);
  // Create ArrayBuffer first, then Uint8Array to ensure proper typing
  const buffer = new ArrayBuffer(rawData.length);
  const outputArray = new Uint8Array(buffer);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

async function sendSubscriptionToBackend(
  subscription: PushSubscription,
  userId: string
): Promise<void> {
  try {
    const { notificationsAPI } = await import("@/lib/services/api.service");
    await notificationsAPI.subscribePush(subscription.toJSON(), userId);
  } catch (error) {
    console.error("[NotificationWorker] Failed to send subscription:", error);
    throw error;
  }
}

// ==============================================================
// Permission Management
// ==============================================================

export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!("Notification" in window)) {
    console.warn("[NotificationWorker] Notifications not supported");
    return "denied";
  }

  if (Notification.permission === "default") {
    const permission = await Notification.requestPermission();
    return permission;
  }

  return Notification.permission;
}

export function getNotificationPermission(): NotificationPermission {
  if (!("Notification" in window)) {
    return "denied";
  }
  return Notification.permission;
}

// ==============================================================
// Message Passing to Service Worker
// ==============================================================

export async function sendMessageToServiceWorker(message: any): Promise<void> {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    if (registration.active) {
      registration.active.postMessage(message);
    }
  } catch (error) {
    console.error("[NotificationWorker] Failed to send message:", error);
  }
}

// ==============================================================
// IndexedDB Helper (for offline storage)
// ==============================================================

export async function getCachedNotifications(): Promise<any[]> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("reacho-notifications-data-v1", 1);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains("notifications")) {
        resolve([]);
        return;
      }

      const tx = db.transaction("notifications", "readonly");
      const store = tx.objectStore("notifications");
      const getAllRequest = store.getAll();

      getAllRequest.onsuccess = () => {
        resolve(getAllRequest.result || []);
      };
      getAllRequest.onerror = () => reject(getAllRequest.error);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains("notifications")) {
        const store = db.createObjectStore("notifications", { keyPath: "id" });
        store.createIndex("created_at", "created_at", { unique: false });
        store.createIndex("is_read", "is_read", { unique: false });
        store.createIndex("type", "type", { unique: false });
      }
    };
  });
}
